import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaSearch } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { http } from '../../lib/http';
import { shopImg, SHOP_PLACEHOLDER } from '../../utils/img';
import ShopRatingDisplay from '../ShopRatingDisplay';
import '../ServiceCommon.css';

const ALL_SERVICE_CATEGORIES = [
  'Barber',
  'Hair & Nail',
  'Laser Clinics',
  'Makeup',
  'Massage',
  'Massage & Aesthetic Medicine',
  'Pet Services',
];

const ALL_TOWNS = [
  'Maadi',
  'Masr El Gedida',
  'Heliopolis',
  'Nasr City',
  'New Cairo',
  'October City',
  'Zamalek',
  'Mohandessin',
  'Dokki',
  'Giza',
];

function sortByRating(list) {
  return [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
}

function mergeOptions(staticList, fromShops) {
  return [...new Set([...staticList, ...fromShops.filter(Boolean)])].sort((a, b) =>
    a.localeCompare(b)
  );
}

export default function AboutShopsSection({ lang }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [town, setTown] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await http.getJson('/businesses?limit=1000&page=1');
        const list = Array.isArray(res?.data) ? res.data : [];
        if (active) setShops(list);
      } catch {
        if (active) setShops([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const categoryOptions = useMemo(
    () => mergeOptions(ALL_SERVICE_CATEGORIES, shops.map((s) => s.serviceCategory)),
    [shops]
  );

  const townOptions = useMemo(
    () => mergeOptions(ALL_TOWNS, shops.map((s) => s.town)),
    [shops]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return shops.filter((shop) => {
      if (category !== 'all' && shop.serviceCategory !== category) return false;
      if (town !== 'all' && shop.town !== town) return false;
      if (query && !(shop.name || shop.slug || '').toLowerCase().includes(query)) return false;
      return Boolean(shop.name || shop.slug);
    });
  }, [shops, category, town, search]);

  const sortedFiltered = useMemo(() => sortByRating(filtered), [filtered]);

  useEffect(() => {
    setExpanded({});
    listRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [category, town, search]);

  const updateScrollButtons = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }

    const scrollPos = Math.abs(el.scrollLeft);
    setCanScrollPrev(scrollPos > 2);
    setCanScrollNext(scrollPos < maxScroll - 2);
  }, []);

  useEffect(() => {
    updateScrollButtons();
  }, [sortedFiltered.length, updateScrollButtons]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      observer.disconnect();
    };
  }, [sortedFiltered.length, updateScrollButtons]);

  const scrollShops = useCallback((direction) => {
    const el = listRef.current;
    if (!el) return;

    const card = el.querySelector('.shop-box');
    const gap = Number.parseFloat(getComputedStyle(el).gap) || 20;
    const step = (card?.offsetWidth || 320) + gap;

    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  return (
    <section className="about-shops-section" id="about-shops" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className="about-shops-top">
          <header className="about-shops-header">
            <h2 className="about-shops-title">{t('aboutShopsTitle')}</h2>
            <p className="about-shops-desc">{t('aboutShopsDesc')}</p>
          </header>
        </div>

        {!loading && (
          <div className="about-shops-toolbar">
            <div className="about-shops-search">
              <FaSearch className="about-shops-search__icon" aria-hidden="true" />
              <input
                type="search"
                className="search-input about-shops-search__input"
                placeholder={t('aboutShopsSearchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t('aboutShopsSearchPlaceholder')}
              />
            </div>
            <div className="about-shops-filters">
              <select
                className="about-shops-filter"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label={lang === 'ar' ? 'فلترة حسب نوع الخدمة' : 'Filter by service type'}
              >
                <option value="all">{t('aboutShopsAllCategories')}</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                className="about-shops-filter"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                aria-label={lang === 'ar' ? 'فلترة حسب المنطقة' : 'Filter by area'}
              >
                <option value="all">{t('aboutShopsAllAreas')}</option>
                {townOptions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {loading && (
          <p className="about-shops-status" role="status">{t('aboutShopsLoading')}</p>
        )}

        {!loading && sortedFiltered.length === 0 && (
          <p className="about-shops-status" role="status">{t('aboutShopsEmpty')}</p>
        )}

        {!loading && sortedFiltered.length > 0 && (
          <>
            <div className="about-shops-carousel">
              <button
                type="button"
                className="about-shops-nav about-shops-nav--prev"
                onClick={() => scrollShops(-1)}
                disabled={!canScrollPrev}
                aria-label={t('aboutShopsPrev')}
              >
                <FaChevronLeft aria-hidden="true" />
              </button>

              <div className="about-shops-scroll">
                <div
                  ref={listRef}
                  className="shop-list about-shops-list"
                  tabIndex={0}
                  role="region"
                  aria-label={t('aboutShopsTitle')}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') {
                      e.preventDefault();
                      scrollShops(-1);
                    } else if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      scrollShops(1);
                    }
                  }}
                >
              {sortedFiltered.map((shop) => {
                const shopId = shop._id || shop.id;
                const shopName = shop.name || shop.slug;
                const isOpen = !!expanded[shopId];
                const services = Array.isArray(shop.services)
                  ? (isOpen ? shop.services : shop.services.slice(0, 3))
                  : [];

                return (
                  <div
                    key={shopId || shopName}
                    className={`shop-box${isOpen ? ' expanded' : ''}${lang === 'ar' ? ' rtl' : ''}`}
                    onClick={() => navigate(`/booking/${encodeURIComponent(shopName)}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/booking/${encodeURIComponent(shopName)}`);
                      }
                    }}
                    role="link"
                    tabIndex={0}
                  >
                    <img
                      className="left-image"
                      src={shopImg(shop.imageUrl || (shop.gallery && shop.gallery[0]))}
                      alt={shopName}
                      loading="lazy"
                      onError={(e) => { e.target.onerror = null; e.target.src = SHOP_PLACEHOLDER; }}
                    />
                    <div className="right-services">
                      <div className="shop-info">
                        <h3 className="shop-name">
                          <span className="shop-title-text">{shopName}</span>
                        </h3>
                        <div className="shop-details">
                          {shop.town && (
                            <span className="shop-location">
                              <FaMapMarkerAlt /> {shop.town}
                            </span>
                          )}
                        </div>
                        {shop.serviceCategory && (
                          <span className="shop-category">{shop.serviceCategory}</span>
                        )}
                        <ShopRatingDisplay shop={shop} lang={lang} />
                      </div>

                      {services.map((s, i) => (
                        <div key={i} className="service-item">
                          <span className="service-name">{s.serviceName}</span>
                          <div className="service-price">
                            <span className="price">{s.price} EGP</span>
                            <button
                              type="button"
                              className="book-mini"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/booking/${encodeURIComponent(shopName)}`);
                              }}
                            >
                              {t('book')}
                            </button>
                          </div>
                        </div>
                      ))}

                      <div className="shop-card-actions">
                        <button
                          type="button"
                          className={`show-more-btn${isOpen ? ' is-open' : ''}`}
                          disabled={!Array.isArray(shop.services) || shop.services.length <= 3}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (Array.isArray(shop.services) && shop.services.length > 3) {
                              setExpanded((p) => ({ ...p, [shopId]: !p[shopId] }));
                            }
                          }}
                        >
                          {isOpen ? t('showLess') : t('showMore')}
                        </button>
                        <button
                          type="button"
                          className="book-now-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/booking/${encodeURIComponent(shopName)}`);
                          }}
                        >
                          {t('recBookNow')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              </div>

              <button
                type="button"
                className="about-shops-nav about-shops-nav--next"
                onClick={() => scrollShops(1)}
                disabled={!canScrollNext}
                aria-label={t('aboutShopsNext')}
              >
                <FaChevronRight aria-hidden="true" />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
